import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { Search, ArrowRightOutlined as ArrowRight } from "@mui/icons-material";
import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import {
  OutlinedInput,
  IconButton,
  InputAdornment,
  Button,
  CircularProgress,
  Typography,
  Stack,
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Paper,
  Tooltip,
  Autocomplete,
  TextField,
  useTheme,
  Tabs,
  Tab,
  Select,
  MenuItem
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { ZoomIn as ViewIcon, Download as DownloadIcon, Edit as EditIcon } from "@mui/icons-material";
import { getMachines, readAsyncStorageValues, getAllServiceTickets, getAllOldServiceTickets } from "features/service/service.slice";
import { Links } from "utils/pageLinkNames";
import DataGridTable from "component/DataGridTable";
import { exportToCsv } from "utils/utilFunctions";

const ServiceTicketsTab = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const { mode } = useSelector((state) => state.globalState);

  const [searchParams, setSearchParams] = useSearchParams();
  const [serviceTickets, setServiceTickets] = useState([]);
  const [showExportAlertDialog, setShowExportAlertDialog] = useState(false);

  const [searchValue, setSearchValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState(1);
  const { user } = useSelector((state) => state.auth);

  const [filteredCounts, setFilteredCounts] = useState({
    all: 0,
    periodic: 0,
    oneTime: 0,
    old: 0,
  });

  const [filterOptions, setFilterOptions] = useState({
    overDue: false,
    upComingIn: null,
    status: [],
    category: [],
  });

  const [filteredData, setFilteredData] = useState([]);

  const { machinesData, appConstants, isLoading, allServiceTickets, allOldServiceTickets } = useSelector((state) => state.service);

  const TAB_CONFIG = {
    1: { key: 'all', categories: ['EQUIPMENT_SUPPORT', 'TASK', 'CALIBRATION', 'INSPECTION', 'INTERNAL_AUDIT', 'PREVENTIVE_MAINTENANCE'] },
    2: { key: 'periodic', categories: ['CALIBRATION', 'INSPECTION', 'INTERNAL_AUDIT', 'PREVENTIVE_MAINTENANCE'] },
    3: { key: 'oneTime', categories: ['EQUIPMENT_SUPPORT', 'TASK'] },
    4: { key: 'old', categories: [] }
  };

  useEffect(() => {
    dispatch(readAsyncStorageValues());
    dispatch(getMachines());
    dispatch(getAllServiceTickets());
    dispatch(getAllOldServiceTickets());
  }, [dispatch]);

  useEffect(() => {
    if (selectedTab === 4 && allOldServiceTickets?.data) loadData(4);
  }, [selectedTab, allOldServiceTickets?.data]);

  useEffect(() => {
    if (allServiceTickets?.data || allOldServiceTickets?.data) {
      loadData(selectedTab);
    }
  }, [selectedTab, allServiceTickets, allOldServiceTickets]);


  const FilterDropdown = ({ label, value, options, onChange }) => {
    const sortedOptions = [...options].sort((a, b) => a.localeCompare(b));
    return (
      <Select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        displayEmpty
        style={{ width: "100%", fontSize: "0.875rem", fontWeight: value ? "normal" : 600 }}
        renderValue={(selected) => selected || <span style={{ fontWeight: 600, fontSize: "0.800rem" }}>{label}</span>}
      >
        <MenuItem value="" style={{ fontWeight: 600 }}>{label}</MenuItem>
        {sortedOptions.map((option) => (
          <MenuItem key={option} value={option}>{option}</MenuItem>
        ))}
      </Select>
    );
  };

  const ticketMatchesFilters = (ticket, assigneUser) => {
    const filters = [
      () => !filterOptions.maintenanceCode?.length || filterOptions.maintenanceCode.includes(ticket?.maintenanceCode),
      () => !filterOptions.category?.length || filterOptions.category.includes(ticket?.ticketCategory),
      () => !filterOptions.status?.length || filterOptions.status.includes(ticket?.status),
      () => !filterOptions.ticketIds?.length || filterOptions.ticketIds.includes(ticket?.ticketId.toString()),
      () => !filterOptions.technician?.length || filterOptions.technician.includes(assigneUser?.nameEmpno),
      () => !filterOptions.modelName?.length || filterOptions.modelName.includes(ticket?.modelName),
      () => !filterOptions.manufacturer?.length || filterOptions.manufacturer.includes(ticket?.manufacturer),
      () => !filterOptions.upComingIn || (ticket.dueDate && moment(ticket.dueDate).isBetween(moment(), moment().add(parseInt(filterOptions.upComingIn), "d"))),
      () => !filterOptions.upComingIn || ticket.dueDate,
      () => !filterOptions.overDue || moment(moment().format("DD MMM YYYY")).isAfter(moment(ticket.dueDate).format("DD MMM YYYY"))
    ];

    return filters.every(filter => filter());
  };

  const ticketBelongsToTab = (ticket, tabNumber) => {
    if (tabNumber === 4) return true;

    const config = TAB_CONFIG[tabNumber];
    const categories = config?.categories.map(cat => appConstants.allTypes.SERVICE_CATEGORY[cat]);
    return categories?.includes(ticket.ticketCategory);
  };

  const transformTicket = (ticket) => {
    const assigneUser = appConstants?.appUsers?.find(user => ticket?.assigneeId === user.userId);

    return {
      ticketId: ticket?.ticketId,
      ticketCategory: ticket.ticketCategory,
      status: ticket.status,
      machineManufacturer: ticket?.manufacturer || "",
      machineModel: ticket?.modelName || "",
      machineModelId: ticket?.modelId || "",
      machineId: ticket?.machineId || "",
      machineSerialNumber: ticket?.serialNumber || "",
      machineMaintenanceCode: ticket?.maintenanceCode || "",
      description: ticket?.description || "",
      dueDate: ticket.dueDate ? moment(ticket.dueDate).format("DD MMM YY") : null,
      dateCreated: moment(ticket.openDate).format("DD MMM YY"),
      scheduledDate: moment(ticket.scheduledDate).format("DD MMM YY"),
      priority: ticket.priority || null,
      vendor: ticket.vendor || null,
      technician: assigneUser?.name || "",
      safetyTicket: ticket?.safetyTicket || null,
    };
  };

  const loadData = (selectedTab) => {
    setLoading(true);
    const ticketsToProcess = selectedTab === 4 ? allOldServiceTickets?.data : allServiceTickets?.data;

    if (!ticketsToProcess) {
      setLoading(false);
      return;
    }

    const serviceTickets = ticketsToProcess
      .filter(ticket => {
        const assigneUser = appConstants?.appUsers?.find(user => ticket?.assigneeId === user.userId);
        return ticketMatchesFilters(ticket, assigneUser) && ticketBelongsToTab(ticket, selectedTab);
      })
      .map(transformTicket)
      .sort((a, b) => b.ticketId - a.ticketId);

    setServiceTickets(serviceTickets);
    updateFilteredCounts();
    setLoading(false);
  };
  const hasActiveFilters = () => {
    return (
      searchValue.trim() !== "" ||
      Object.values(filterOptions).some(value =>
        Array.isArray(value) ? value.length > 0 : value !== null && value !== false
      )
    );
  };

  const getCountForTab = (tabNumber) => {
    const data = tabNumber === 4 ? allOldServiceTickets?.data : allServiceTickets?.data;
    if (!data) return 0;

    return data.filter(ticket => ticketBelongsToTab(ticket, tabNumber)).length;
  };

  const initializeAllCounts = () => {
    if (allServiceTickets?.data && appConstants?.allTypes?.SERVICE_CATEGORY) {
      setFilteredCounts({
        all: getCountForTab(1),
        periodic: getCountForTab(2),
        oneTime: getCountForTab(3),
        old: getCountForTab(4),
      });
    }
  };

  const updateFilteredCounts = () => {
    if (!allServiceTickets?.data || !appConstants?.allTypes?.SERVICE_CATEGORY) return;

    const data = selectedTab === 4 ? allOldServiceTickets?.data : allServiceTickets?.data;
    if (!data) return;

    const tabKey = TAB_CONFIG[selectedTab]?.key;

    if (!hasActiveFilters()) {
      initializeAllCounts();
      setFilteredData([]);
      return;
    }

    const filtered = data.filter(ticket => {
      const assigneUser = appConstants?.appUsers?.find(user => ticket?.assigneeId === user.userId);

      if (!ticketBelongsToTab(ticket, selectedTab)) return false;

      if (searchValue.trim() !== '') {
        const searchLower = searchValue.toLowerCase();
        const searchFields = [
          ticket.ticketId?.toString(),
          ticket.description,
          ticket.maintenanceCode,
          ticket.manufacturer,
          ticket.modelName,
          assigneUser?.name
        ];

        const matchesSearch = searchFields.some(field =>
          field?.toLowerCase().includes(searchLower)
        );

        if (!matchesSearch) return false;
      }

      return ticketMatchesFilters(ticket, assigneUser);
    });

    setFilteredCounts(prev => ({ ...prev, [tabKey]: filtered.length }));
    setFilteredData(filtered.map(transformTicket));
  };

  useEffect(() => {
    if (allServiceTickets?.data?.length) {
      initializeAllCounts();
    }
  }, [allServiceTickets]);

  useEffect(() => {
    if (allServiceTickets?.data || allOldServiceTickets?.data) {
      updateFilteredCounts();
    }
  }, [selectedTab, allServiceTickets, allOldServiceTickets, filterOptions, searchValue]);

  useEffect(() => {
    const ticketCategoryArrayParam = searchParams.getAll("ticketCategory[]");
    const maintenanceCodeArrayParam = searchParams.getAll("maintenanceCode[]");
    const ticketStatusArrayParam = searchParams.getAll("ticketStatus[]");
    const ticketIdArrayParam = searchParams.getAll("ticketId[]");
    setFilterOptions({
      ...filterOptions,
      category: ticketCategoryArrayParam,
      maintenanceCode: maintenanceCodeArrayParam,
      status: ticketStatusArrayParam,
      ticketIds: ticketIdArrayParam,
    });
  }, []);

  const getCurrentTabData = () => {
    const data = selectedTab === 4 ? allOldServiceTickets?.data || [] : allServiceTickets?.data || [];
    return data.filter(ticket => ticketBelongsToTab(ticket, selectedTab));
  };

  const renderGridActions = (data) => {
    return (
      <div>
        <IconButton sx={{ mr: 1 }} onClick={() => navigate(`/${Links.SERVICE_TICKET}/` + data.row.ticketId)}>
          <Tooltip title="View">
            <ViewIcon sx={{ color: mode === "dark" ? "#fff" : theme.palette.primary[50] }} />
          </Tooltip>
        </IconButton>
        <IconButton
          sx={{ mr: 1 }}
          onClick={() => navigate(`/${Links.UPDATE_SERVICE_TICKET}/` + data.row.ticketId, { state: { enableEditing: true } })}
        >
          <Tooltip title="Edit">
            <EditIcon sx={{ color: mode === "dark" ? "#fff" : theme.palette.primary[50] }} />
          </Tooltip>
        </IconButton>
      </div>
    );
  };

  const handleSearchChange = (event) => setSearchValue(event.target.value);

  const handleClearSearch = () => setSearchValue("");

  const handleTabsChange = (event, newValue) => {
    setSelectedTab(newValue);
    handleClearSearch();
    setFilterOptions({});
  };

  const clearAllFilters = () => {
    setFilterOptions({
      maintenanceCode: [],
      manufacturer: [],
      modelName: [],
      technician: [],
      category: [],
      status: [],
      upComingIn: null,
      overDue: false,
      ticketIds: [],
    });
    setSearchValue('');
  };

  useEffect(() => {
    loadData(selectedTab);
  }, [selectedTab, filterOptions, searchValue]);

  const dateComparator = (v1, v2) => moment(v1).diff(moment(v2));
  const columnAttributes = [
    {
      field: "ticketId",
      headerName: "ID",
      minWidth: 60,
      flex: 0.3,
    },
    {
      field: "dateCreated",
      headerName: "Open Date",
      minWidth: 90,
      flex: 0.4,
      sortComparator: dateComparator,
    },
    {
      field: "scheduledDate",
      headerName: "Scheduled Date",
      minWidth: 90,
      flex: 0.4,
      sortComparator: dateComparator,
    },
    {
      field: "dueDate",
      headerName: "Due Date",
      minWidth: 90,
      flex: 0.4,
      sortComparator: dateComparator,
    },
    {
      field: "machineMaintenanceCode",
      headerName: "Maintenance Code",
      minWidth: 120,
      editType: "dropdownedit",
      textAlign: "left",
      flex: 0.6,
      renderHeader: () => {
        const currentData = getCurrentTabData();
        const maintenanceCodes = [...new Set(currentData.map((t) => t.maintenanceCode).filter(Boolean))];

        return (
          <FilterDropdown
            label="Maintenance Code"
            value={filterOptions.maintenanceCode?.[0] || ''}
            options={maintenanceCodes}
            onChange={(selected) => {
              const selectedArray = selected ? [selected] : [];
              setFilterOptions((prev) => ({ ...prev, maintenanceCode: selectedArray }));
            }}
            showClearAll={true}
            onClearAll={clearAllFilters}
          />
        );
      },

      renderCell: (params) => {
        return (
          <div
            onClick={() => navigate(`/${Links.EDIT_MACHINE}/${params.row.machineId}`)}
            style={{ cursor: "pointer", textDecoration: "none", textAlign: "center", width: "100%" }}
            onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
            onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
          >
            {params.value}
          </div>
        );
      },
    },
    {
      field: "description",
      headerName: "Description",
      minWidth: 110,
      editType: "dropdownedit",
      textAlign: "left",
      flex: 0.5,
    },
    ...(appConstants?.showManufacturerModelInAssetList === true ? [
      {
        field: "machineManufacturer",
        headerName: "Manufacturer",
        minWidth: 120,
        flex: 0.6,
        renderHeader: () => {
          const currentData = getCurrentTabData();
          const Manufacturers = [...new Set(currentData.map((t) => t.manufacturer).filter(Boolean))];
          return (
            <FilterDropdown
              label="Manufacturer"
              value={filterOptions.manufacturer?.[0] || ""}
              options={Manufacturers}
              onChange={(selected) => {
                const selectedArray = selected ? [selected] : [];
                setFilterOptions((prev) => ({ ...prev, manufacturer: selectedArray }));
              }}
            />
          );
        }
      },
      {
        field: "machineModel",
        headerName: "Model",
        minWidth: 120,
        flex: 0.5,
        renderHeader: () => {
          const currentData = getCurrentTabData();
          const models = [...new Set(currentData.map((t) => t.modelName).filter(Boolean))];
          return (
            <FilterDropdown
              label="Model"
              value={filterOptions.modelName?.[0] || ""}
              options={models}
              onChange={(selected) => {
                const selectedArray = selected ? [selected] : [];
                setFilterOptions((prev) => ({ ...prev, modelName: selectedArray }));
              }}
            />
          );
        },
        renderCell: (params) => {
          return (
            <div
              onClick={() => navigate(`/${Links.MACHINE_MODEL}/${params.row.machineModelId}`)}
              style={{ cursor: "pointer", textDecoration: "none" }}
              onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
              onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
            >
              {params.value}
            </div>
          );
        },
      },
    ] : []),
    {
      field: "technician",
      headerName: "Technician",
      minWidth: 120,
      flex: 0.5,
      renderHeader: () => {
        const currentData = getCurrentTabData();
        const technicians = [
          ...new Set(
            currentData
              .map((t) => {
                const assignee = appConstants?.appUsers?.find((u) => u.userId === t.assigneeId);
                return assignee?.nameEmpno;
              })
              .filter(Boolean)
          )
        ];
        return (
          <FilterDropdown
            label="Technician"
            value={filterOptions.technician?.[0] || ""}
            options={technicians}
            onChange={(selected) => {
              const selectedArray = selected ? [selected] : [];
              setFilterOptions((prev) => ({ ...prev, technician: selectedArray }));
            }}
          />
        )
      }
    },
    {
      field: "ticketCategory",
      headerName: "Category",
      minWidth: 90,
      flex: 0.5,
      renderHeader: () => {
        const currentData = getCurrentTabData();
        const categories = [...new Set(currentData.map((t) => t.ticketCategory).filter(Boolean))];
        return (
          <FilterDropdown
            label="Category"
            value={filterOptions.category?.[0] || ""}
            options={categories}
            onChange={(value) => {
              const selectedArray = value ? [value] : [];
              setFilterOptions((prev) => ({ ...prev, category: selectedArray }));
            }}
          />
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 120,
      flex: 0.5,
      renderHeader: () => {
        const currentData = getCurrentTabData();
        const statuses = [...new Set(currentData.map((t) => t.status).filter(Boolean))];
        return (
          <FilterDropdown
            label="Status"
            value={filterOptions.status?.[0] || ""}
            options={statuses}
            onChange={(selected) => {
              const selectedArray = selected ? [selected] : [];
              setFilterOptions((prev) => ({ ...prev, status: selectedArray }));
            }}
          />
        );

      }

    },
    {
      field: "vendor",
      headerName: "Vendor",
      minWidth: 90,
      valueGetter: (params) => {
        const vendorId = params.row?.vendor?.id || params.row?.vendor;
        const customer = appConstants.customers.find((user) => user.id === vendorId);
        return customer?.customerName || "Internal";
      },
      flex: 0.3,
    },
    {
      field: "Actions",
      headerName: "Actions",
      width: 140,
      flex: 0.7,
      sortable: false,
      filterable: false,
      renderCell: renderGridActions,
      disableClickEventBubbling: true,
    },
  ];
  const downloadCsv = (e, exportAll = false) => {
    e.preventDefault();
    const serviceTicketsList = [];
    if (exportAll) {
      allServiceTickets.data.forEach((ticket) => {
        const machine = machinesData.find((machine) => machine.id === ticket?.machine?.id);
        const assigneUser = appConstants?.appUsers?.find((user) => {
          return ticket?.assignees?.[0] === user.userId;
        });
        return allServiceTickets.data.push({
          ticketId: ticket?.ticketId,
          ticketCategory: ticket?.ticketCategory,
          status: ticket?.status,
          machineManufacturer: ticket?.manufacturer || "",
          machineModel: ticket?.model || "",
          machineSerialNumber: ticket?.serialNumber || "",
          machineMaintenanceCode: ticket?.maintenanceCode || "",
          description: ticket?.description || "",
          dueDate: moment(ticket.dueDate).format("DD MMM YYYY"),
          dateCreated: moment(ticket.openDate).format("DD MMM YYYY"),
          scheduledDate: moment(ticket.scheduledDate).format("DD MMM YYYY"),
          technician: assigneUser?.name,
        });
      });
    }
    const headers = [
      "Ticket Id,Open Date, Scheduled Date, Maintenance Code,Description, Manufacturer,Model,Serial Number,Technician, Category, Status",
    ];
    const keys = [
      "ticketId",
      "dateCreated",
      "scheduledDate",
      "machineMaintenanceCode",
      "description",
      "machineManufacturer",
      "machineModel",
      "machineSerialNumber",
      "technician",
      "ticketCategory",
      "status",
    ];
    exportToCsv(headers, keys, exportAll ? serviceTicketsList : serviceTickets, "ServiceTickets");
    setShowExportAlertDialog(false);
  };
  const filterTickets = (filterOption, filterItem) => {
    setFilterOptions({ ...filterOptions, [filterOption]: filterOptions[filterOption].filter((item) => item !== filterItem) });
  };
  const renderCustomFilter = (filterOption) => (
    <Paper>
      {filterOptions &&
        filterOptions[filterOption] &&
        filterOptions[filterOption].length &&
        filterOption !== "category" &&
        filterOption !== "status" &&
        filterOption !== "maintananceCode" &&
        filterOption !== "ticketIds" &&
        filterOption !== "priority" ? (
        <Stack direction={"row"} flexWrap={"wrap"} alignItems={"center"} px={0.5} py={filterOptions && filterOptions.overDue ? 0 : 0.3}>
          <Typography m={1} textTransform={"capitalize"}>
            {filterOption}
          </Typography>
          {filterOptions[filterOption].map((filterItem) => (
            <Stack direction={"row"} alignItems={"center"} pl={1} borderRadius={10} backgroundColor="#00808030">
              <Typography variant="caption">{filterItem}</Typography>
              <IconButton onClick={() => filterTickets(filterOption, filterItem)}>
                <Tooltip title="Clear item">
                  <CloseIcon sx={{ fontSize: 15 }} />
                </Tooltip>
              </IconButton>
            </Stack>
          ))}
        </Stack>
      ) : null}
    </Paper>
  );

  if (isLoading)
    return (
      <div
        style={{
          textAlign: "center",
          paddingTop: 200,
        }}
      >
        <CircularProgress color="secondary" />
      </div>
    );
  return (
    <>
      <Box sx={{ mx: 2 }}>
        <Stack direction={"row"} justifyContent={"space-between"} my={2}>
          <Stack direction={"row"} flexWrap={"wrap"} alignItems={"center"} gap={2}>
            <Box>
              <Button
                startIcon={<AddIcon />}
                sx={{ height: "self" }}
                autoCapitalize="none"
                variant="contained"
                onClick={() =>
                  navigate(`/${Links.CREATE_SERVICE_TICKET}/`, {
                    state: { isCreatingTicket: true },
                  })
                }
              >
                Create
              </Button>
            </Box>

            <OutlinedInput
              id="outlined-adornment-password"
              value={searchValue}
              onChange={(event) => handleSearchChange(event)}
              placeholder="Search..."
              startAdornment={
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              }
              endAdornment={searchValue && (
                <InputAdornment position="end">
                  <IconButton onClick={handleClearSearch} edge="end">
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              )}
              sx={{ minWidth: 300, maxWidth: 600, height: 35 }}
            />

            <Paper>
              <Stack
                direction={"row"}
                backgroundColor={
                  filterOptions && filterOptions.overDue
                    ? mode === "dark"
                      ? theme.palette.grey[700]
                      : theme.palette.primary[700]
                    : mode === "dark"
                      ? theme.palette.grey[10]
                      : theme.palette.primary[200]
                }
                alignItems={"center"}
                px={0.5}
                py={filterOptions && filterOptions.overDue ? 0 : 0.3}
              >
                <Tooltip title="Filter over due tickets">
                  <IconButton onClick={() => setFilterOptions({ ...filterOptions, overDue: true })} size="small">
                    <Typography sx={{ color: "#fff" }}>Over due</Typography>
                  </IconButton>
                </Tooltip>
                {filterOptions && filterOptions.overDue ? (
                  <IconButton onClick={() => setFilterOptions({ ...filterOptions, overDue: false })}>
                    <Tooltip title="Clear filter">
                      <CloseIcon sx={{ fontSize: 18, color: "#fff" }} />
                    </Tooltip>
                  </IconButton>
                ) : null}
              </Stack>
            </Paper>

            <Autocomplete
              disablePortal
              size="small"
              id="combo-box-demo"
              onChange={(event, newValue, reason) => {
                setFilterOptions({ ...filterOptions, upComingIn: reason === "clear" ? null : parseInt(newValue) });
              }}
              value={filterOptions && filterOptions.upComingIn ? filterOptions.upComingIn : ""}
              options={[7, 15, 30, 60, 90]}
              sx={{ minWidth: 150, maxWidth: 600 }}
              renderInput={(params) => <TextField {...params} label="Upcoming in" style={{}} />}
            />
            <Button
              startIcon={<CloseIcon />}
              sx={{ height: "self" }}
              autoCapitalize="none"
              variant="contained"
              onClick={clearAllFilters}
            >
              Clear All
            </Button>
            {Object.keys(filterOptions).map((filterOption) => renderCustomFilter(filterOption))}
          </Stack>
          <Stack>
            <Button
              startIcon={<DownloadIcon />}
              sx={{ minWidth: 170 }}
              autoCapitalize="none"
              variant="contained"
              onClick={(e) => {
                const ifFilterExists = Object.keys(filterOptions).filter((item) =>
                  filterOptions[item]
                    ? filterOptions[item] === true || filterOptions[item] > 0
                      ? filterOptions[item]
                      : filterOptions[item].length
                    : filterOptions[item]
                );
                ifFilterExists.length ? setShowExportAlertDialog(true) : downloadCsv(e);
              }}
            >
              Export to csv
            </Button>
          </Stack>
        </Stack>

        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs value={selectedTab} onChange={handleTabsChange} aria-label="service tickets tabs" centered>
            <Tab
              sx={{
                fontWeight: "bold",
                marginRight: "20px",
                "&:hover": {
                  backgroundColor: "initial",
                  color: "initial",
                },
              }}
              label={`60 days (${filteredCounts.all})`}
              value={1}
            />
            <Tab
              sx={{
                fontWeight: "bold",
                marginRight: "20px",
                "&:hover": {
                  backgroundColor: "initial",
                  color: "initial",
                },
              }}
              label={`Periodic Service Tickets (${filteredCounts.periodic})`}
              value={2}
            />
            <Tab
              sx={{
                fontWeight: "bold",
                marginRight: "20px",
                "&:hover": {
                  backgroundColor: "initial",
                  color: "initial",
                },
              }}
              label={`One Time Service Tickets (${filteredCounts.oneTime})`}
              value={3}
            />
            <Tab
              sx={{
                fontWeight: "bold",
                marginRight: "20px",
                "&:hover": {
                  backgroundColor: "initial",
                  color: "initial",
                },
              }}
              label={`More Than 60 Days (${filteredCounts.old})`}
              value={4}
            />
          </Tabs>
        </Box>
        <>
          <DataGridTable
            loading={loading}
            rowId={"ticketId"}
            data={hasActiveFilters() ? filteredData : serviceTickets}
            columnAttributes={columnAttributes}
          />
        </>
        <Dialog open={showExportAlertDialog} onClose={() => setShowExportAlertDialog(false)}>
          <DialogContent>
            <Typography mt={1}>Click "Export filtered" to export only the filtered tickets.</Typography>
            <Typography mt={1}>Click "Export all" to export all tickets.</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowExportAlertDialog(false)}>Cancel</Button>
            <Button variant="contained" onClick={(e) => downloadCsv(e)}>
              Export filtered
            </Button>
            <Button variant="contained" onClick={(e) => downloadCsv(e, true)}>
              Export all
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};
export default ServiceTicketsTab;
